import type { JSONContent } from "@tiptap/core";
import type { Schema } from "@tiptap/pm/model";
import {
  prosemirrorJSONToYXmlFragment,
  yDocToProsemirrorJSON,
} from "@tiptap/y-tiptap";
import { isEqual } from "lodash";
import * as Y from "yjs";
import {
  resolveDynamicVersionComparisonContent,
  type VersionComparisonDynamicContentResolver,
} from "./dynamic-version-comparison";

const TIPTAP_DEFAULT_FIELD = "default";
const TIPTAP_PERMANENT_USER_DATA_MAP_FIELD = "__tiptapcollab__users";
const DOVETAIL_DYNAMIC_CONTENT_AUTHOR_ID = "dovetail";

type NativeVersionComparisonSnapshot = {
  snapshot: Uint8Array;
};

type NativeEarlierVersionComparisonSnapshot =
  NativeVersionComparisonSnapshot & { fromVersion: number };

type NativeLaterVersionComparisonSnapshot = NativeVersionComparisonSnapshot & {
  toVersion: number | null;
};

export type NativeDynamicVersionComparison = {
  earlierContent: JSONContent;
  laterContent: JSONContent;
  prevSnapshot: Uint8Array;
  snapshot: Uint8Array;
};

const createYjsDocumentFromSnapshots = (
  initialSnapshot: Uint8Array,
  subsequentSnapshot?: Uint8Array
) => {
  const document = new Y.Doc({ gc: false });

  Y.applyUpdateV2(document, initialSnapshot);
  if (subsequentSnapshot) {
    Y.applyUpdateV2(document, subsequentSnapshot);
  }

  return document;
};

const assignFreshYjsClientId = (document: Y.Doc) => {
  const clientDocument = new Y.Doc();
  document.clientID = clientDocument.clientID;
  clientDocument.destroy();
};

const waitForYjsUserAttribution = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });

const replaceYjsContentWithAttribution = async ({
  authorId,
  content,
  document,
  schema,
}: {
  authorId: string;
  content: JSONContent;
  document: Y.Doc;
  schema: Schema;
}) => {
  if (isEqual(yDocToProsemirrorJSON(document, TIPTAP_DEFAULT_FIELD), content)) {
    return;
  }

  assignFreshYjsClientId(document);
  const authorClientId = document.clientID;
  new Y.PermanentUserData(
    document,
    document.getMap(TIPTAP_PERMANENT_USER_DATA_MAP_FIELD)
  ).setUserMapping(document, authorClientId, authorId, {
    filter: () => document.clientID === authorClientId,
  });
  prosemirrorJSONToYXmlFragment(
    schema,
    content,
    document.getXmlFragment(TIPTAP_DEFAULT_FIELD)
  );
  await waitForYjsUserAttribution();
};

const containsIntermediateOnlyNestedElement = ({
  prevSnapshot,
  snapshot,
}: {
  prevSnapshot: Uint8Array;
  snapshot: Uint8Array;
}) => {
  const historyDocument = new Y.Doc({ gc: false });
  Y.applyUpdateV2(historyDocument, prevSnapshot);
  const earlierState = Y.snapshot(historyDocument);
  Y.applyUpdateV2(historyDocument, snapshot);
  const laterState = Y.snapshot(historyDocument);
  const unionState = new Y.Snapshot(earlierState.ds, laterState.sv);
  const root = historyDocument.getXmlFragment(TIPTAP_DEFAULT_FIELD);

  const containsIntermediateOnlyDescendant = (
    parent: Y.XmlElement
  ): boolean => {
    const earlierChildren = new Set(
      Y.typeListToArraySnapshot(parent, earlierState)
    );
    const laterChildren = new Set(
      Y.typeListToArraySnapshot(parent, laterState)
    );

    return Y.typeListToArraySnapshot(parent, unionState).some((child) => {
      if (!(child instanceof Y.XmlElement)) {
        return false;
      }

      const isVisibleEarlier = earlierChildren.has(child);
      const isVisibleLater = laterChildren.has(child);

      if (!isVisibleEarlier && !isVisibleLater) {
        return true;
      }

      return (
        isVisibleEarlier &&
        isVisibleLater &&
        containsIntermediateOnlyDescendant(child)
      );
    });
  };
  const earlierRootChildren = new Set(
    Y.typeListToArraySnapshot(root, earlierState)
  );
  const laterRootChildren = new Set(
    Y.typeListToArraySnapshot(root, laterState)
  );
  const containsIntermediateOnlyElement = Y.typeListToArraySnapshot(
    root,
    unionState
  ).some(
    (child) =>
      child instanceof Y.XmlElement &&
      earlierRootChildren.has(child) &&
      laterRootChildren.has(child) &&
      containsIntermediateOnlyDescendant(child)
  );

  historyDocument.destroy();
  return containsIntermediateOnlyElement;
};

const buildComparisonSnapshotsFromResolvedDynamicContent = async ({
  earlierContent,
  laterContent,
  schema,
}: {
  earlierContent: JSONContent;
  laterContent: JSONContent;
  schema: Schema;
}) => {
  const document = new Y.Doc({ gc: false });
  await replaceYjsContentWithAttribution({
    authorId: DOVETAIL_DYNAMIC_CONTENT_AUTHOR_ID,
    content: earlierContent,
    document,
    schema,
  });
  const prevSnapshot = Y.encodeStateAsUpdateV2(document);

  await replaceYjsContentWithAttribution({
    authorId: DOVETAIL_DYNAMIC_CONTENT_AUTHOR_ID,
    content: laterContent,
    document,
    schema,
  });
  const snapshot = Y.encodeStateAsUpdateV2(document);

  document.destroy();
  return { prevSnapshot, snapshot };
};

export const buildDynamicContentVersionComparison = async ({
  earlier,
  later,
  latestTiptapVersion,
  resolveDynamicContentForVersion,
  schema,
}: {
  earlier: NativeEarlierVersionComparisonSnapshot;
  later: NativeLaterVersionComparisonSnapshot;
  latestTiptapVersion: number;
  resolveDynamicContentForVersion: VersionComparisonDynamicContentResolver;
  schema: Schema;
}): Promise<NativeDynamicVersionComparison | null> => {
  try {
    const earlierRawDocument = createYjsDocumentFromSnapshots(earlier.snapshot);
    const laterRawDocument = createYjsDocumentFromSnapshots(
      earlier.snapshot,
      later.snapshot
    );
    const earlierContent = yDocToProsemirrorJSON(
      earlierRawDocument,
      TIPTAP_DEFAULT_FIELD
    ) as JSONContent;
    const laterContent = yDocToProsemirrorJSON(
      laterRawDocument,
      TIPTAP_DEFAULT_FIELD
    ) as JSONContent;
    const [resolvedEarlierVersion, resolvedLaterVersion] = await Promise.all([
      resolveDynamicVersionComparisonContent({
        content: earlierContent,
        latestTiptapVersion,
        resolveDynamicContentForVersion,
        version: earlier.fromVersion,
      }),
      resolveDynamicVersionComparisonContent({
        content: laterContent,
        latestTiptapVersion,
        resolveDynamicContentForVersion,
        version: later.toVersion,
      }),
    ]);

    earlierRawDocument.destroy();
    laterRawDocument.destroy();

    if (
      !resolvedEarlierVersion ||
      !resolvedLaterVersion ||
      (!resolvedEarlierVersion.hasDynamicNodes &&
        !resolvedLaterVersion.hasDynamicNodes)
    ) {
      return null;
    }

    const comparisonDocument = createYjsDocumentFromSnapshots(earlier.snapshot);

    if (resolvedEarlierVersion.hasDynamicNodes) {
      await replaceYjsContentWithAttribution({
        authorId: DOVETAIL_DYNAMIC_CONTENT_AUTHOR_ID,
        content: resolvedEarlierVersion.content,
        document: comparisonDocument,
        schema,
      });
    }
    let prevSnapshot = Y.encodeStateAsUpdateV2(comparisonDocument);

    Y.applyUpdateV2(comparisonDocument, later.snapshot);
    await replaceYjsContentWithAttribution({
      authorId: DOVETAIL_DYNAMIC_CONTENT_AUTHOR_ID,
      content: resolvedLaterVersion.content,
      document: comparisonDocument,
      schema,
    });
    let snapshot = Y.encodeStateAsUpdateV2(comparisonDocument);

    comparisonDocument.destroy();
    // Yjs retains nested elements that were created and deleted between the
    // compared versions, even though they are visible in neither endpoint.
    // Tiptap Snapshot Compare throws when it encounters one while traversing
    // the combined history, so rebuild only that unsafe history from the two
    // resolved documents.
    if (
      containsIntermediateOnlyNestedElement({
        prevSnapshot,
        snapshot,
      })
    ) {
      ({ prevSnapshot, snapshot } =
        await buildComparisonSnapshotsFromResolvedDynamicContent({
          earlierContent: resolvedEarlierVersion.content,
          laterContent: resolvedLaterVersion.content,
          schema,
        }));
    }

    return {
      earlierContent: resolvedEarlierVersion.content,
      laterContent: resolvedLaterVersion.content,
      prevSnapshot,
      snapshot,
    };
  } catch (error) {
    console.error("Unable to build dynamic content version comparison", error);
    return null;
  }
};
